import { useEffect, useRef } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

let _scriptState = 'idle';
const _waiters = [];
let _cachedKey = null;

const fetchMapsKey = async () => {
  if (_cachedKey) return _cachedKey;
  const res = await fetch(`${getApiBase()}/api/config/maps-key`, {
    headers: { ...getTenantHeaders() }
  });
  if (!res.ok) throw new Error('Maps key fetch failed');
  const data = await res.json();
  if (!data.key) throw new Error('Maps key empty');
  _cachedKey = data.key;
  return _cachedKey;
};

const ensureGoogleMapsLoaded = (apiKey) => {
  if (_scriptState === 'ready') return Promise.resolve();
  if (_scriptState === 'error') return Promise.reject(new Error('Maps script failed'));
  if (_scriptState === 'loading') {
    return new Promise((res, rej) => _waiters.push({ res, rej }));
  }

  _scriptState = 'loading';
  return new Promise((res, rej) => {
    _waiters.push({ res, rej });

    // Raise PAC container above all app modals (one-time injection)
    if (!document.getElementById('jfn-pac-z')) {
      const s = document.createElement('style');
      s.id = 'jfn-pac-z';
      s.textContent = '.pac-container { z-index: 99999 !important; }';
      document.head.appendChild(s);
    }

    const cbName = '__jfnMapsReady';
    window[cbName] = () => {
      _scriptState = 'ready';
      delete window[cbName];
      _waiters.forEach(w => w.res());
      _waiters.length = 0;
    };

    const el = document.createElement('script');
    el.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${cbName}`;
    el.async = true;
    el.defer = true;
    el.onerror = () => {
      _scriptState = 'error';
      _waiters.forEach(w => w.rej(new Error('Maps script load failed')));
      _waiters.length = 0;
    };
    document.head.appendChild(el);
  });
};

const parsePlace = (place) => {
  const get = (type, short = false) => {
    const comp = (place.address_components || []).find(c => c.types.includes(type));
    return comp ? (short ? comp.short_name : comp.long_name) : '';
  };
  const street = [get('street_number'), get('route')].filter(Boolean).join(' ');
  const city = get('locality') || get('sublocality_level_1') || get('neighborhood');
  const state = get('administrative_area_level_1', true);
  const zip = get('postal_code');
  const formatted = place.formatted_address || '';
  return { street, city, state, zip, formatted };
};

const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  inputStyle,
  placeholder,
  name,
  required,
  disabled,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    let live = true;
    if (!inputRef.current) return;

    fetchMapsKey()
      .then((apiKey) => ensureGoogleMapsLoaded(apiKey))
      .then(() => {
        if (!live || !inputRef.current || !window.google?.maps?.places?.Autocomplete) return;
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
        });
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          if (place?.address_components) {
            onAddressSelect(parsePlace(place));
          }
        });
      })
      .catch(() => { /* degrade to plain input on script failure */ });

    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <input
      ref={inputRef}
      style={inputStyle}
      placeholder={placeholder}
      value={value}
      name={name}
      required={required}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default AddressAutocomplete;
